import { type FC, useState } from "react";
import { IconButton, Menu } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import { ActionMenuItem } from "@/shared/components/BulkActions";
import { useStreamEventProductsQuery } from "@/partner/entities/products";
import { CancelEventDialog } from "@/partner/features/products";
import { useAbility } from "@/partner/shared/lib";
import { getCancellableEventProducts } from "../domains";

interface StreamEventCancelMenuProps {
  productIds: string[];
}

/**
 * Меню эфира «Отменить событие»: пункт на каждый ещё не отменённый
 * PPV-продукт эфира, пункт открывает то же окно отмены, что и карточка
 * продукта. Только с правом EventCancel (admin организации).
 */
export const StreamEventCancelMenu: FC<StreamEventCancelMenuProps> = ({
  productIds,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();
  const allowed = ability.can("create", "EventCancel");
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [productId, setProductId] = useState<string | null>(null);

  const { data } = useStreamEventProductsQuery({
    variables: { ids: productIds },
    skip: !allowed || productIds.length === 0,
  });
  const products = getCancellableEventProducts(data?.products.nodes ?? []);

  if (!allowed || products.length === 0) return null;

  return (
    <>
      <IconButton
        aria-label={t("streams.eventCancel.actionsLabel")}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ ml: 1 }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {products.map((product) => (
          <ActionMenuItem
            key={product.id}
            onClick={() => {
              setAnchor(null);
              setProductId(product.id);
            }}
          >
            {t("streams.eventCancel.menuItem", { title: product.title })}
          </ActionMenuItem>
        ))}
      </Menu>
      {productId && (
        <CancelEventDialog
          productId={productId}
          open
          onClose={() => setProductId(null)}
          onStarted={() => setProductId(null)}
        />
      )}
    </>
  );
};
